// "You're seated at Spades table N." (SPA 10d): a strip under the navbar on every _Layout page while the visitor holds
// a seat at a casual table, with a link back to it. Not on the table itself, nor on the lobby (which sends a seated
// visitor to their table): their routes' handles say hidesSeatedBanner.
//
// The seat outlives the table's page: leaving it in place keeps the seat for the server's grace period (16 s), then,
// before the game starts, gives it up (the host's leaving closes the table); during a game the player stays seated
// with an emptied time bank until the game ends. Nothing tells the other pages, so the banner asks
// (GET /api/play/spades/my-table, owner: casual tables only): when the app starts signed in, on leaving a page that
// hides it (and again once that grace period is over), when the window regains focus, and every 30 s while it shows.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useMatches } from "react-router";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { getJson } from "@core/api";
import { tablePath } from "../routes/spades/casual";
import { matchedHandle } from "./handles";
import { useSession } from "./session";

export const MY_TABLE_URL = "/api/play/spades/my-table";
/** How often it asks again while it shows (owner): the table can close, or the game end, behind the page. */
export const SEATED_POLL_MS = 30_000;
/** When it asks again after leaving a table's page: the server's ReconnectGracePeriod (16 s), and a second. */
export const AFTER_GRACE_PERIOD_MS = 17_000;

/** The banner's height, on the root element while it shows: the play pages' drawer frame fills the screen under the
 * navbar and the banner, so it has to know how tall the banner is (it wraps on a narrow screen). */
export const SEATED_BANNER_HEIGHT_PROPERTY = "--seated-banner-h";

export interface MyTable {
    tableId: number | null;
}

/** The casual table the signed-in visitor is seated at (per user, so signing in as someone else asks afresh). */
export function myTableQuery(userName: string | null) {
    return queryOptions({
        queryKey: ["my-table", userName],
        queryFn: () => getJson<MyTable>(MY_TABLE_URL),
        // Read again whenever it's wanted (see the top of the file); a failure just leaves the banner as it was
        staleTime: 0,
        retry: false,
        refetchInterval: query => query.state.data?.tableId != null ? SEATED_POLL_MS : false,
    });
}

export function SeatedBanner() {
    const session = useSession();
    const hidden = matchedHandle(useMatches()).hidesSeatedBanner === true;
    const enabled = session.isSignedIn && !hidden;
    const { data, dataUpdatedAt, refetch } = useQuery({ ...myTableQuery(session.userName), enabled });

    // The answer cached before arriving (or before a page that hid it) may be out of date (the visitor has since left
    // their table, or sat at one), so only an answer read since counts. (State derived from a change, as RequireAuth.)
    const [shownSince, setShownSince] = useState({ hidden, answeredAt: dataUpdatedAt });
    if (shownSince.hidden !== hidden)
        setShownSince({ hidden, answeredAt: dataUpdatedAt });

    // Left a page that hides it (a table): read now (the query is enabled again), and once the grace period is over.
    const wasHidden = useRef(hidden);
    useEffect(() => {
        const left = wasHidden.current && !hidden;
        wasHidden.current = hidden;
        if (!left || !session.isSignedIn)
            return;

        const timer = setTimeout(() => void refetch(), AFTER_GRACE_PERIOD_MS);
        return () => clearTimeout(timer);
    }, [hidden, session.isSignedIn]);

    const tableId = data?.tableId;
    const shows = enabled && tableId != null && dataUpdatedAt > shownSince.answeredAt;

    // Publish its height while it shows (before paint, so the drawer frame never overflows the screen for a frame),
    // and again whenever it changes (it wraps as the window narrows); gone, there's nothing to leave room for
    const bannerRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
        const element = bannerRef.current;
        if (!shows || element == null)
            return;

        const root = document.documentElement;
        const publish = () => root.style.setProperty(SEATED_BANNER_HEIGHT_PROPERTY, `${element.getBoundingClientRect().height}px`);
        publish();
        const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publish);
        observer?.observe(element);
        return () => {
            observer?.disconnect();
            root.style.removeProperty(SEATED_BANNER_HEIGHT_PROPERTY);
        };
    }, [shows]);

    if (!shows)
        return null;

    return (
        <div id="seated-banner" role="status" ref={bannerRef}>
            <span>You're seated at Spades table {tableId}.</span>
            <a href={tablePath(tableId)} className="ac-btn ac-btn-go ac-btn-sm" tabIndex={0}>Return to table</a>
        </div>
    );
}
